/* eslint-disable jira/filename-case */

import type {
	Action,
	ActionThunk,
	BaseContainerProps,
	GenericContainerComponent,
	Store,
	StoreBehavior,
	StateInitializer,
	StoreActionApi,
} from '../types.tsx';
import hash from '../utils/hash.tsx';

function createKey<TState, TActions extends Record<string, ActionThunk<TState, TActions>>>(
	initialState: TState,
	actions: TActions,
	name: string,
) {
	const src = !name ? Object.keys(actions).reduce((acc, k) => acc + actions[k].toString(), '') : '';
	return [name, hash(src + JSON.stringify(initialState))].filter(Boolean).join('__');
}

type ActionDispatcher<TState> = (
	action: (api: StoreActionApi<TState>) => void,
) => ({ dispatch }: StoreActionApi<TState>) => void;
/**
 * creates react-sweet-state "dispatch pattern" action
 * @param actions - other actions of the store
 * @agent-skills react-sweet-state/action-pattern
 */
export const actionsWithDispatch: <
	TState,
	TActions extends Record<string, ActionThunk<TState, TActions>> = {},
>(
	actions: TActions,
) => TActions & { dispatch: ActionDispatcher<TState> } = <TState, TActions>(actions = {}) =>
	// oxlint-disable-next-line typescript/consistent-type-assertions
	({
		...actions,
		dispatch:
			(action) =>
			({ dispatch }) =>
				dispatch(action),
	}) as TActions & { dispatch: ActionDispatcher<TState> };

export function createStore<
	TState extends object,
	TActions extends Record<string, ActionThunk<TState, TActions>>,
	TContainerProps = unknown,
>({
	name = '',
	initialState,
	actions,
	containedBy,
	handlers = {},
	behavior,
}:
	| {
			initialState: StateInitializer<TState>;
			actions: TActions;
			name?: string;
			containedBy?: never;
			handlers?: never;
			behavior?: StoreBehavior;
	  }
	| {
			initialState: StateInitializer<TState>;
			actions: TActions;
			name?: string;
			containedBy: GenericContainerComponent<TContainerProps>;
			handlers?: {
				/**
				 * @deprecated Warning. If onInit/onUpdate performs an async action you must specify
				 * getIsConcurrentSafe returning `true`. Do not configure getIsConcurrentSafe in case of
				 * synchronous flow. See https://hello.atlassian.net/wiki/spaces/JDP/pages/4802027965/ADR+react-sweet-state+RSS+concurrent+mode+compatibility
				 */
				// oxlint-disable-next-line @typescript-eslint/no-explicit-any
				onInit?: () => Action<TState, TContainerProps, any>;
				/**
				 * @deprecated Warning. If onInit/onUpdate performs an async action you must specify
				 * getIsConcurrentSafe returning `true`. Do not configure getIsConcurrentSafe in case of
				 * synchronous flow. See https://hello.atlassian.net/wiki/spaces/JDP/pages/4802027965/ADR+react-sweet-state+RSS+concurrent+mode+compatibility
				 */
				// oxlint-disable-next-line @typescript-eslint/no-explicit-any
				onUpdate?: () => Action<TState, TContainerProps, any>;
				// oxlint-disable-next-line @typescript-eslint/no-explicit-any
				onDestroy?: () => Action<TState, TContainerProps, any>;
				/**
				 * @deprecated Warning. If onInit/onContainerUpdate performs an async action you must specify
				 * getIsConcurrentSafe returning `true`. Do not configure getIsConcurrentSafe in case of
				 * synchronous flow. See https://hello.atlassian.net/wiki/spaces/JDP/pages/4802027965/ADR+react-sweet-state+RSS+concurrent+mode+compatibility
				 */
				onContainerUpdate?: (
					nextProps: BaseContainerProps & TContainerProps,
					prevProps: BaseContainerProps & TContainerProps,
					// oxlint-disable-next-line @typescript-eslint/no-explicit-any
				) => Action<TState, TContainerProps, any>;
			};
			behavior?: StoreBehavior;
	  }): Store<TState, TActions, TContainerProps> {
	let key: string;
	return {
		get key() {
			// lazy evaluate key on first access
			return key || (key = createKey<any, TActions>(initialState, actions, name)); // oxlint-disable-line @typescript-eslint/no-explicit-any
		},
		// @ts-expect-error TS2322: Type StateInitializer<TState> is not assignable to type TState
		initialState,
		actions,
		containedBy,
		handlers,
		behavior,
	};
}
